import { Editor } from '@monaco-editor/react';
import * as Popover from '@radix-ui/react-popover';
import { BarChart as BarChartIcon, FileText } from 'lucide-react';
import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ResultsTable } from '@shadcn/components/results-table';
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@shadcn/components/ui/chart';
import { useRunQuery } from '@shadcn/hooks/use-run-query';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Select } from '~/components/ui/Select';
import { Skeleton } from '~/components/ui/Skeleton';
import { Switch } from '~/components/ui/Switch';
import { ToggleGroup, ToggleGroupItem } from '~/components/ui/ToggleGroup';
import { themeIsDark } from '~/lib/stores/theme';

interface SqlEditorProps {
  initialSql?: string;
  defaultLimit?: number;
  queryKey?: any;
  label?: ReactNode;
  onResults?: (data: any[] | undefined) => void;
  onRowClick?: (row: any, queryKey?: any) => void;
  hideSql?: boolean;
  readOnly?: boolean;
  runAutomatically?: boolean;
  refetch?: number;
  initialNaturalLanguageMode?: boolean;
  hideChartOption?: boolean;
}

export function SqlEditor({
  initialSql,
  defaultLimit,
  queryKey,
  onResults,
  onRowClick,
  label = 'Query your data',
  hideSql = false,
  readOnly = false,
  runAutomatically = false,
  refetch,
  hideChartOption = false,
}: SqlEditorProps) {
  const [sql, setSql] = useState(initialSql || '');
  const [isSqlVisible, setIsSqlVisible] = useState(!hideSql);

  const { mutate: runQuery, data, isPending, error } = useRunQuery();
  const [isChartVisible, setIsChartVisible] = useState(false);
  const [xAxisColumn, setXAxisColumn] = useState<string | null>(null);
  const [yAxisColumn, setYAxisColumn] = useState<string | null>(null);

  const columns = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    return Object.keys(data[0]);
  }, [data]);

  useEffect(() => {
    if (initialSql) {
      setSql(initialSql);
    }
  }, [initialSql]);

  const handleRunQuery = useCallback(() => {
    if (sql) {
      runQuery({ query: sql });
    }
  }, [sql, runQuery]);

  useEffect(() => {
    setIsSqlVisible(!hideSql);
  }, [hideSql]);

  useEffect(() => {
    if (runAutomatically && initialSql) {
      runQuery({ query: initialSql });
    }
  }, [runAutomatically, initialSql, runQuery]);

  useEffect(() => {
    if (refetch && refetch > 0) {
      handleRunQuery();
    }
  }, [refetch, handleRunQuery]);

  useEffect(() => {
    if (onResults) {
      onResults(data);
    }
  }, [data, onResults]);

  useEffect(() => {
    const noResults = !data || (Array.isArray(data) && data.length === 0);

    if (noResults && !isSqlVisible && !readOnly && !isPending) {
      setIsSqlVisible(true);
    }
  }, [data, isSqlVisible]);

  const serverErrorMessage = (error as any)?.response?.data?.message || '';
  const isReadOnlyError = serverErrorMessage.includes('permission denied') || serverErrorMessage.includes('42501');
  const customReadOnlyError = "You can't directly alter your database schema, use chat instead";

  // Build the toggle-group selection based on current UI state
  const toggleValues = useMemo(() => {
    const values: string[] = [];

    if (isSqlVisible) {
      values.push('sql');
    }

    if (!hideChartOption && isChartVisible) {
      values.push('chart');
    }

    return values;
  }, [isSqlVisible, isChartVisible, hideChartOption]);

  const handleToggleGroupChange = (values: string[]) => {
    setIsSqlVisible(values.includes('sql'));
  };
  return (
    <div>
      <div className="px-6 pt-4 lg:px-8 lg:pt-8">
        <div className="flex items-center gap-4 mb-4    ">
          <h2 className="text-base text-bolt-ds-textPrimary flex-1">{label}</h2>
          {!hideChartOption && (
            <ToggleGroup type="multiple" value={toggleValues} onValueChange={handleToggleGroupChange} className="gap-1">
              <ToggleGroupItem value="sql" aria-label="SQL">
                <FileText className="h-4 w-4" />
              </ToggleGroupItem>
              <Popover.Root modal={true}>
                <Popover.Trigger asChild>
                  <ToggleGroupItem value="chart" aria-label="Chart">
                    <BarChartIcon className="h-4 w-4" />
                  </ToggleGroupItem>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content className="w-80 bg-bolt-ds-surfaceOne z-max p-4 rounded-md border border-bolt-ds-borderOutline">
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1">
                            <div className="flex-1 mb-2 block">Show Chart</div>
                            <p className="text-xs text-bolt-ds-textSecondary">Visualize your data with a chart.</p>
                          </div>
                          <Switch
                            id="show-chart"
                            size="sm"
                            className="mb-0"
                            checked={isChartVisible}
                            onCheckedChange={setIsChartVisible}
                          />
                        </div>
                        {isChartVisible && (
                          <>
                            {columns.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-4 mb-2">
                                  <Select
                                    label="X-Axis"
                                    labelPosition="side"
                                    containerClassName="h-8"
                                    labelClassName="text-nowrap"
                                    onValueChange={setXAxisColumn}
                                    defaultValue={xAxisColumn || undefined}
                                    placeholder="Select column"
                                    options={columns.map((col) => ({
                                      label: col,
                                      value: col,
                                    }))}
                                  />
                                </div>
                                <div className="flex items-center gap-4 mb-2">
                                  <Select
                                    onValueChange={setYAxisColumn}
                                    defaultValue={yAxisColumn || undefined}
                                    label="Y-Axis"
                                    labelClassName="text-nowrap"
                                    labelPosition="side"
                                    placeholder="Select column"
                                    containerClassName="h-8"
                                    options={columns.map((col) => ({
                                      label: col,
                                      value: col,
                                    }))}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-bolt-ds-textSecondary">
                                No data. Please, run your query first.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </ToggleGroup>
          )}
        </div>
        <div>
          {isSqlVisible && (
            <div className="border-t border-b border-bolt-elements-borderColor bg-bolt-ds-surfaceThree overflow-hidden -mx-6 lg:-mx-8 mt-4 relative">
              <Editor
                height="200px"
                language="sql"
                value={sql}
                onChange={(value) => setSql(value || '')}
                theme={themeIsDark() ? 'vs-dark' : 'vs'}
                className="bg-transparent"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  readOnly,
                  padding: {
                    top: 24,
                    bottom: 24,
                  },
                }}
              />
              <Button
                size="md"
                onClick={handleRunQuery}
                loading={isPending}
                disabled={isPending}
                className="absolute bottom-4 right-4"
              >
                {isPending ? 'Running...' : 'Run Query'}
              </Button>
            </div>
          )}
        </div>
      </div>
      {isPending && (
        <div className="space-y-2 p-4 px-6 lg:px-8">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}

      {error && (
        <div className="px-6 lg:px-8 py-4">
          <Alert type="danger" title="Query Error">
            {isReadOnlyError
              ? customReadOnlyError
              : serverErrorMessage || (error as any)?.message || 'An unexpected error occurred'}
          </Alert>
        </div>
      )}

      {!hideChartOption && data && isChartVisible && xAxisColumn && yAxisColumn && (
        <div className="px-8 mt-8 mb-4">
          <QueryResultChart data={data} xAxis={xAxisColumn} yAxis={yAxisColumn} />
        </div>
      )}

      {data && (
        <div>
          <ResultsTable
            data={data}
            onRowClick={(row) => onRowClick?.(row, queryKey)}
            isDefaultQuery={initialSql === sql}
            defaultLimit={defaultLimit}
          />
        </div>
      )}
    </div>
  );
}

function QueryResultChart({ data, xAxis, yAxis }: { data: any[]; xAxis: string; yAxis: string }) {
  const chartConfig = {
    [yAxis]: {
      label: yAxis,
      color: `#0451a5`,
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
      <BarChart
        accessibilityLayer
        data={data}
        margin={{
          left: -24,
          right: 12,
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis dataKey={xAxis} tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
        <YAxis dataKey={yAxis} tickLine={false} axisLine={false} tickMargin={8} tickCount={5} allowDecimals={false} />
        <ChartTooltip content={<ChartTooltipContent className="w-[150px]" indicator="dot" />} />
        <Bar dataKey={yAxis} fill={`var(--color-${yAxis})`} />
      </BarChart>
    </ChartContainer>
  );
}
